"use client";

import { useFeatureFlag } from "@posthog/next";
import { useRoutedGailAppUrl } from "@/providers/AppLinkRoutingProvider";
import {
  type DemoButtonVariant,
  type VariantLocaleEntry,
  type VariantPayload,
} from "@/types/demoButtonVariant";
import { useTranslation } from "react-i18next";

const DEMO_BUTTON_FLAG = "marketing-demo-btn-text";

function parsePayload(raw: unknown): VariantPayload | null {
  if (raw == null) return null;

  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (typeof parsed !== "object" || Array.isArray(parsed)) return null;
  return parsed as VariantPayload;
}

function normalizeEntry(
  entry: VariantLocaleEntry | undefined,
): DemoButtonVariant | null {
  if (!entry) return null;
  if (typeof entry === "string") {
    return { title: entry };
  }
  if (typeof entry === "object" && typeof entry.title === "string") {
    return {
      title: entry.title,
      href: typeof entry.href === "string" ? entry.href : undefined,
    };
  }
  return null;
}

export function useDemoButtonVariant(): DemoButtonVariant | null {
  const flag = useFeatureFlag(DEMO_BUTTON_FLAG);
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const payload = parsePayload(flag?.payload);
  if (!payload) return null;
  return normalizeEntry(payload[lang]);
}

export function useDemoButtonText(): string | null {
  const variant = useDemoButtonVariant();
  return variant?.title ?? null;
}

export function useDemoButtonHref(fallback: string): string {
  const variant = useDemoButtonVariant();
  return useRoutedGailAppUrl(variant?.href ?? fallback);
}

function isAbsoluteUrl(href: string): boolean {
  return /^(https?:)?\/\//i.test(href);
}

export type DemoButtonLink = {
  href: string;
  target: "_blank" | "_self";
};

export function useDemoButtonLink(fallback: string): DemoButtonLink {
  const href = useDemoButtonHref(fallback);
  return {
    href,
    target: isAbsoluteUrl(href) ? "_blank" : "_self",
  };
}
